import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── News Data ────────────────────────────────────────────────────────────────
// Add more items here and the grid will auto-adjust (1 → centered, 2+ → grid)
export const policyPapersNews = [
  {
    id: 'eu-pact-migration-asylum-2026',
    image: 'https://i.postimg.cc/wxsVp5d7/EUImage.jpg',
    tag: 'Policy Papers',
    title: 'EU Pact on Migration and Asylum Enters into Application',
    excerpt:
      "On 12 June 2026, the European Union's Pact on Migration and Asylum entered into application, introducing a new common framework for migration management, asylum procedures and cooperation among EU Member States.",
    date: '12 June, 2026',
    featured: true,
    body: [
      'On 12 June 2026, the European Union\'s Pact on Migration and Asylum entered into application, introducing a new common framework for migration management, asylum procedures and cooperation among EU Member States.',
      'The new rules aim to strengthen border management, streamline asylum procedures, improve coordination among Member States and reinforce mechanisms for managing migratory pressures across the European Union.',
      'As a chamber committed to promoting informed dialogue and cooperation between Italy and Bangladesh, BICCI will continue to monitor regulatory developments that may be relevant to businesses, institutions and communities engaged in Italy–Bangladesh relations.',
    ],
    source: 'https://t.co/MCJN2tRo4b',
  },
];

// ─── Shared Reveal Logic ──────────────────────────────────────────────────────
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll<HTMLElement>('[data-r]').forEach((node, i) => {
            setTimeout(() => {
              node.style.opacity = '1';
              node.style.transform = 'translateY(0) translateX(0)';
            }, i * 120);
          });
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

const hide = (dir: 'up' | 'left' | 'right' = 'up'): React.CSSProperties => ({
  opacity: 0,
  transform:
    dir === 'up'
      ? 'translateY(28px)'
      : dir === 'left'
      ? 'translateX(-32px)'
      : 'translateX(32px)',
  transition: 'opacity 0.8s ease, transform 0.8s ease',
});

// ─── Component ────────────────────────────────────────────────────────────────
export default function NewsSection() {
  const sectionRef = useReveal();
  const navigate = useNavigate();

  const isSingle = policyPapersNews.length === 1;

  return (
    <section id="news" className="py-20 md:py-24 font-sans" style={{ backgroundColor: '#F8F7F3' }}>
      <div
        ref={sectionRef}
        className="w-full px-6 md:px-10 lg:px-16 xl:px-24 max-w-[1400px] mx-auto overflow-hidden"
      >
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-12">
          <h2
            data-r
            style={{
              ...hide('up'),
              color: '#0F1C35',
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              letterSpacing: '-0.02em',
            }}
          >
            News
          </h2>
          <p
            data-r
            style={{
              ...hide('up'),
              color: '#3D4A62',
              fontSize: '1rem',
              marginTop: '0.5rem',
            }}
          >
            Latest updates and insights from BICCI
          </p>
        </div>

        {/* Cards Grid — centered when single, auto-grid when multiple */}
        <div
          data-r
          style={hide('up')}
          className={
            isSingle
              ? 'flex justify-center'
              : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
          }
        >
          {policyPapersNews.map((item) => (
            <a
              key={item.id}
              href={`/news/policy-papers`}
              onClick={(e) => {
                e.preventDefault();
                navigate('/news/policy-papers', { state: { articleId: item.id } });
              }}
              className="group flex-shrink-0 bg-white rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer flex flex-col"
              style={{
                borderColor: '#E5E7EB',
                width: isSingle ? 'min(340px, 100%)' : undefined,
              }}
            >
              {/* Image */}
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
                <div className="absolute top-3 right-3">
                  <span
                    className="px-2.5 py-1 bg-white/95 backdrop-blur-md text-[10px] font-bold uppercase tracking-wider rounded-full shadow-sm"
                    style={{ color: '#0077b6' }}
                  >
                    {item.featured && '★ '}
                    {item.tag}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="p-5 md:p-6 flex flex-col flex-grow">
                <span className="text-[11px] font-bold text-gray-400 mb-2.5 block">
                  {item.date}
                </span>
                <h3
                  className="text-base md:text-lg font-bold mb-2.5 line-clamp-2 leading-snug"
                  style={{ color: '#0F1C35' }}
                >
                  {item.title}
                </h3>
                <p
                  className="text-sm leading-relaxed line-clamp-3 mb-5 flex-grow"
                  style={{ color: '#3D4A62' }}
                >
                  {item.excerpt}
                </p>

                {/* Read More — uses #0077b6 matching Join BICCI button */}
                <span
                  className="inline-flex items-center gap-1.5 text-sm font-bold transition-colors mt-auto"
                  style={{ color: '#0077b6' }}
                >
                  Read More
                  <svg
                    className="w-4 h-4 transition-transform group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}